import { useEffect, useState } from "react";
import { LuKey, LuLayoutDashboard, LuRefreshCw, LuLock, LuActivity, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { API_URL } from "../services/api";

interface UsageRecord {
    id: string;
    target: string;
    scrapeType: string;
    scrapeGoal: string;
    status: "completed" | "canceled" | "in-progress";
    createdAt: string;
    timestamp: string;
    creditsSpent: number;
    totalLookups: number;
    resultCount: number;
    source: "api_key" | "dashboard";
    apiKeyId: string | null;
    apiKeyName: string | null;
}

interface UsageResponse {
    apiKeyUsage: UsageRecord[];
    dashboardUsage: UsageRecord[];
    totals: {
        apiKeySpent: number;
        dashboardSpent: number;
        apiKeyJobs: number;
        dashboardJobs: number;
    };
}

const GOAL_LABELS: Record<string, string> = {
    emails: "Emails",
    profiles: "Profiles",
    list: "List",
};

function StatusPill({ status }: { status: string }) {
    const map: Record<string, string> = {
        completed: "bg-green-100 text-green-700",
        canceled: "bg-red-100 text-red-600",
        "in-progress": "bg-amber-100 text-amber-700",
    };
    const label = status === "in-progress" ? "Partial / Stopped" : status === "canceled" ? "Canceled" : "Completed";
    return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${map[status] || "bg-gray-100 text-gray-600"}`}>
            {label}
        </span>
    );
}

function formatWhen(iso: string, fallback: string) {
    const d = new Date(iso || fallback);
    if (isNaN(d.getTime())) return fallback || "—";
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
}

function UsageTable({
    title,
    icon,
    accent,
    records,
    totalSpent,
    showKeyColumn,
    emptyText,
}: {
    title: string;
    icon: React.ReactNode;
    accent: string;
    records: UsageRecord[];
    totalSpent: number;
    showKeyColumn: boolean;
    emptyText: string;
}) {
    return (
        <div className="w-full min-w-0 flex flex-col bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Header / total */}
            <div className={`px-5 py-4 border-b border-gray-100 flex items-center justify-between ${accent}`}>
                <div className="flex items-center gap-2.5">
                    <span className="text-white/90">{icon}</span>
                    <div>
                        <h2 className="text-white font-semibold text-sm leading-tight">{title}</h2>
                        <p className="text-white/70 text-[11px]">{records.length} job{records.length === 1 ? "" : "s"}</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-white/70 text-[10px] uppercase tracking-widest">Total spent</p>
                    <p className="text-white font-mono font-bold text-lg leading-tight">{totalSpent.toFixed(2)}<span className="text-white/70 text-xs ml-1">cr</span></p>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto overflow-y-auto max-h-[60vh] custom-scrollbar">
                {records.length === 0 ? (
                    <div className="py-16 text-center text-gray-400 text-sm">{emptyText}</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-50 z-10">
                            <tr className="text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Target</th>
                                <th className="px-4 py-3">Goal</th>
                                {showKeyColumn && <th className="px-4 py-3">API Key</th>}
                                <th className="px-4 py-3 text-right">Lookups</th>
                                <th className="px-4 py-3 text-right">Credits</th>
                                <th className="px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {records.map((r) => (
                                <tr key={r.id} className="hover:bg-gray-50/70 transition-colors">
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs font-mono">{formatWhen(r.createdAt, r.timestamp)}</td>
                                    <td className="px-4 py-3 font-medium text-gray-800 max-w-[160px] truncate" title={r.target}>{r.target}</td>
                                    <td className="px-4 py-3 text-gray-600">{GOAL_LABELS[r.scrapeGoal] || r.scrapeGoal}</td>
                                    {showKeyColumn && (
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-xs font-medium">
                                                {r.apiKeyName || "—"}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-4 py-3 text-right font-mono text-gray-600">{r.totalLookups}</td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">{r.creditsSpent.toFixed(2)}</td>
                                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default function UsageTracker() {
    const [data, setData] = useState<UsageResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    // 0 = API Key usage, 1 = Dashboard usage. Arrows flip between the two.
    const [activeView, setActiveView] = useState(0);

    const fetchUsage = async () => {
        setIsLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${API_URL}/api/scrapes/usage`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (json.success) {
                setData(json.data);
            } else {
                setError(json.message || "Failed to load usage");
            }
        } catch (err: any) {
            console.error(err);
            setError("Failed to load usage data.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsage();
    }, []);

    const totals = data?.totals;

    return (
        <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
            {/* Page header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2">
                        <LuActivity className="text-violet-600 w-6 h-6" />
                        <h1 className="text-2xl font-bold text-gray-900">Usage Tracker</h1>
                    </div>
                    <p className="text-gray-500 text-sm mt-1 flex items-center gap-1.5">
                        <LuLock className="w-3.5 h-3.5" />
                        Permanent audit log of every scrape and the credits it consumed. Entries cannot be deleted.
                    </p>
                </div>
                <button
                    onClick={fetchUsage}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors disabled:opacity-50"
                >
                    <LuRefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            {/* Summary cards */}
            {totals && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Total Credits Spent</p>
                        <p className="text-2xl font-bold text-gray-900 font-mono mt-1">{(totals.apiKeySpent + totals.dashboardSpent).toFixed(2)}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                        <p className="text-[10px] font-bold text-violet-500 uppercase tracking-widest">Via API Keys</p>
                        <p className="text-2xl font-bold text-violet-700 font-mono mt-1">{totals.apiKeySpent.toFixed(2)}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{totals.apiKeyJobs} jobs</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                        <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Via Dashboard</p>
                        <p className="text-2xl font-bold text-blue-700 font-mono mt-1">{totals.dashboardSpent.toFixed(2)}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{totals.dashboardJobs} jobs</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 text-red-600 text-sm border border-red-100">{error}</div>
            )}

            {/* Single full-width table with arrow controls to switch view */}
            <div>
                {/* Switcher bar */}
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => setActiveView(v => (v + 1) % 2)}
                        className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm text-gray-600 hover:bg-gray-50 hover:text-violet-600 transition-colors"
                        aria-label="Previous view"
                    >
                        <LuChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-3">
                        {[0, 1].map((i) => (
                            <button
                                key={i}
                                onClick={() => setActiveView(i)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                    activeView === i
                                        ? i === 0
                                            ? "bg-violet-600 text-white shadow-md"
                                            : "bg-blue-600 text-white shadow-md"
                                        : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
                                }`}
                            >
                                {i === 0 ? <LuKey className="w-4 h-4" /> : <LuLayoutDashboard className="w-4 h-4" />}
                                {i === 0 ? "API Key Usage" : "Dashboard Usage"}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setActiveView(v => (v + 1) % 2)}
                        className="flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm text-gray-600 hover:bg-gray-50 hover:text-violet-600 transition-colors"
                        aria-label="Next view"
                    >
                        <LuChevronRight className="w-5 h-5" />
                    </button>
                </div>

                {/* Active table */}
                {activeView === 0 ? (
                    <UsageTable
                        title="API Key Usage"
                        icon={<LuKey className="w-5 h-5" />}
                        accent="bg-gradient-to-r from-violet-600 to-purple-600"
                        records={data?.apiKeyUsage || []}
                        totalSpent={totals?.apiKeySpent || 0}
                        showKeyColumn={true}
                        emptyText="No API-key scrapes yet."
                    />
                ) : (
                    <UsageTable
                        title="Dashboard Usage"
                        icon={<LuLayoutDashboard className="w-5 h-5" />}
                        accent="bg-gradient-to-r from-blue-600 to-cyan-600"
                        records={data?.dashboardUsage || []}
                        totalSpent={totals?.dashboardSpent || 0}
                        showKeyColumn={false}
                        emptyText="No dashboard scrapes yet."
                    />
                )}
            </div>
        </div>
    );
}
